import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

const PRINCIPLES = [
  {
    title: "The real problem is drift",
    body:
      "Most people do not stop because they miss once. They stop because one miss turns into avoidance, and avoidance turns into disengagement.",
    icon: <WarningAmberRoundedIcon color="warning" />,
  },
  {
    title: "The next step should stay small",
    body:
      "TrustBud is designed to lower the threshold to restart. The app should give you something you can do right now, not something you have to psych yourself up for.",
    icon: <BoltRoundedIcon color="primary" />,
  },
  {
    title: "Recovery matters more than perfection",
    body:
      "The point is not flawless adherence. The point is noticing a slip early and getting back in before the whole thing fades out.",
    icon: <CheckCircleRoundedIcon color="success" />,
  },
];

const SECTIONS = [
  {
    title: "Today helps you act",
    paragraphs: [
      "Today answers: what should I do now?",
      "Use Today to see what is waiting, what is done, and what to start next. When the full habit feels too big, keep it alive with a lighter step.",
    ],
  },
  {
    title: "Insights helps you understand yourself",
    paragraphs: [
      "Insights answers: what is TrustBud learning about me?",
      "It reads your goals, habits, timing, and follow-through so you can see what works, where you fall off, and what to change next.",
    ],
  },
  {
    title: "Connections are your people",
    paragraphs: [
      "Connections answers: who are my TrustBuds?",
      "It is your accountability circle: TrustBuds, connections, requests, and people you can start accountability with.",
    ],
  },
  {
    title: "Activity shows what is happening",
    paragraphs: [
      "Activity answers: what is happening around me?",
      "It shows who is active, who could use a check-in, recent accountability activity, messages, and Daily Report movement.",
    ],
  },
  {
    title: "Accountability keeps follow-through visible",
    paragraphs: [
      "An Accountability agreement is a short reporting loop with one TrustBud or a group.",
      "Daily Reports, proof, resets, and visible no-report days make follow-through harder to ignore.",
    ],
  },
  {
    title: "Plan shapes the work",
    paragraphs: [
      "Plan answers: what am I committed to?",
      "Use Plan to adjust your vision, goals, habits, schedule, and planned times when the setup needs to change.",
    ],
  },
  {
    title: "Weekly Summary carries the lesson forward",
    paragraphs: [
      "Weekly Summary answers: what happened this week?",
      "Use it to see what moved, what slipped, and what to carry into next week.",
    ],
  },
  {
    title: "What TrustBud Is",
    paragraphs: [
      "TrustBud is an accountability product built around one specific job: preventing people from silently falling off and helping them get back in quickly.",
      "It does not just record what you planned or what you completed. It is designed to notice when follow-through is slipping and respond with one clear way forward.",
      "The product is grounded in real behavior: people miss, avoid, delay, and disappear. TrustBud is for the moment before that becomes the new normal.",
    ],
  },
  {
    title: "What You Put In",
    paragraphs: [
      "You still define a direction and the habits that matter to you. That structure gives the app something real to support.",
      "Visions, goals, and habits exist to support follow-through. They matter, but they are not the main point of the product.",
      "The product is most useful when your commitments are concrete enough that one small step can still count as re-entry.",
    ],
  },
  {
    title: "How The App Responds",
    paragraphs: [
      "TrustBud looks for the moments when follow-through is weakening: nothing has happened yet today, you missed yesterday, or you have gone quiet long enough that pressure needs to change.",
      "When that happens, the app resolves one clear nudge and one primary move. The goal is clarity, not volume.",
      "That primary move might be a quick check-in, one completed habit, or one short update.",
    ],
  },
  {
    title: "Daily Check-ins",
    paragraphs: [
      "A check-in is a lightweight note that you are still in the loop.",
      "In solo mode, that can be enough to satisfy the day. In Accountability, it is weaker on its own because the relationship is built around visible follow-through, not just presence.",
      "Check-ins are useful because they make silence visible early.",
    ],
  },
  {
    title: "Habits",
    paragraphs: [
      "Habits are the smallest repeatable behaviors connected to your goals.",
      "TrustBud works best when habits are practical and close enough to real life that you can still do one even on a rough day.",
      "That is what makes the app good at recovery: it can point you to something small and meaningful instead of a big abstract reset.",
    ],
  },
  {
    title: "Connections and Accountability",
    paragraphs: [
      "TrustBud separates your accountability circle from the active agreements where Daily Reports are visible.",
      "Connections can include multiple TrustBuds, past accountability partners, and people who may need support. Accountability is the focused reporting loop you start with one person or a group.",
      "Proof, report deadlines, and leaderboards can add structure when the agreement needs more pressure.",
      "The active Accountability agreement is where reporting, visibility, and recovery matter most.",
    ],
  },
  {
    title: "Activity and Re-entry",
    paragraphs: [
      "When Accountability is active, a short update can be enough to reopen the loop.",
      "These interactions are intentionally lightweight. The product should not make recovery feel like a ritual you have to earn.",
      "The important thing is that both people can see that the line has not gone dead.",
    ],
  },
  {
    title: "Today, Accountability, and Activity",
    paragraphs: [
      "Today is where the product answers: what matters right now?",
      "Accountability is where a focused reporting agreement becomes visible between people, especially when repair or re-entry is needed.",
      "Activity is where TrustBud answers: what is happening around me right now?",
    ],
  },
  {
    title: "Insights",
    paragraphs: [
      "Insights is the Plus layer that turns behavior into a practical adjustment.",
      "It should not feel like more charts. It should feel like TrustBud noticed a pattern, gave you one useful adjustment, helped apply it, and then checked whether it worked.",
      "The goal is practical insight: make the plan easier, recover faster, involve the right person when helpful, and learn from the result.",
    ],
  },
  {
    title: "Recovery and Nudges",
    paragraphs: [
      "TrustBud uses nudges as interventions, not just reminders.",
      "The strongest nudges are tied to drift, missed activity, re-entry, and Accountability recovery state instead of generic motivation.",
      "Recovery can happen solo or through accountability. The app should make it easier to get back in, not easier to disappear.",
    ],
  },
  {
    title: "Privacy and Visibility",
    paragraphs: [
      "TrustBud is built around accountability, but not everything should be public or comparative.",
      "The product aims to keep partner visibility useful without turning the experience into a scoreboard.",
      "You still control profile and discovery visibility through your settings.",
    ],
  },
];

export default function HowTrustBudWorksPage() {
  return (
    <Box sx={{ maxWidth: 880, mx: "auto", px: { xs: 1.5, sm: 0 }, pb: 5 }}>
      <Stack spacing={2} sx={{ mb: 4 }}>
        <Typography
          variant="overline"
          sx={{ color: "primary.main", fontWeight: 800, letterSpacing: 0 }}
        >
          Product Overview
        </Typography>
        <Typography
          variant="h3"
          sx={{ fontWeight: 900, letterSpacing: 0, lineHeight: 1.02 }}
        >
          How TrustBud works now.
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
          TrustBud helps catch drift early, lower the cost of restarting, and make accountability real through one other person when it helps.
        </Typography>
      </Stack>

      <Grid container spacing={3} sx={{ mb: 5 }}>
        {PRINCIPLES.map((item) => (
          <Grid item xs={12} md={4} key={item.title}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                borderRadius: "8px",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "#ffffff",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Box>{item.icon}</Box>
                  <Typography variant="h6" fontWeight={800}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.body}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ mb: 3 }} />

      <Stack spacing={1.5}>
        {SECTIONS.map((section, index) => (
          <Accordion
            key={section.title}
            defaultExpanded={index === 0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "#ffffff",
              "&:before": { display: "none" },
              overflow: "hidden",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={700}>
                {section.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                {section.paragraphs.map((text, textIndex) => (
                  <Typography
                    key={`${section.title}-${textIndex}`}
                    variant="body2"
                    color="text.secondary"
                  >
                    {text}
                  </Typography>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5 }}>
        You control what others see through your privacy and discovery settings. The goal is accountability with psychological safety, not exposure.
      </Typography>
    </Box>
  );
}
